import { AnalyticsProvider } from '@services/analytics-provider';
import { stringFromError } from '@services/utils';
import { TRACKING_ENABLED } from '@services/environment';

/**
 * PostHog analytics provider implementation
 */
export class PostHogProvider implements AnalyticsProvider {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private posthog: any = null;

  constructor() {
    // Only import PostHog when tracking is enabled
    if (TRACKING_ENABLED) {
      import('posthog-js')
        .then(posthogModule => {
          this.posthog = posthogModule.default;
        })
        .catch(error => {
          console.warn('Failed to import PostHog:', error);
        });
    }
  }

  private shouldTrack(): boolean {
    return TRACKING_ENABLED;
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      this.posthog.capture(eventName, properties);
    } catch (error) {
      console.warn('PostHog event tracking failed:', error);
    }
  }

  trackPageView(pageName: string, properties?: Record<string, unknown>): void {
    this.trackEvent('page_viewed', {
      page_name: pageName,
      ...properties,
    });
  }

  identifyUser(userId: string, properties?: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      this.posthog.identify(userId, properties);
    } catch (error) {
      console.warn('PostHog user identification failed:', error);
    }
  }

  updateUserProfile(properties: Record<string, unknown>): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      this.posthog.setPersonProperties(this.sanitizeProperties(properties));
    } catch (error) {
      console.warn('PostHog user profile update failed:', error);
    }
  }

  trackError(error: Error | unknown, context?: Record<string, unknown>): void {
    const errorMessage =
      error instanceof Error ? error.message : stringFromError(error);
    const errorName = error instanceof Error ? error.name : 'Unknown';
    const errorStack = error instanceof Error ? error.stack : 'No stack trace';

    this.trackEvent('error_occurred', {
      error_message: errorMessage,
      error_name: errorName,
      error_stack: errorStack,
      ...context,
    });

    if (this.posthog) {
      try {
        this.posthog.captureException(
          error instanceof Error ? error : new Error(String(error))
        );
      } catch (captureError) {
        console.warn('PostHog exception capture failed:', captureError);
      }
    }
  }

  reset(): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    try {
      this.posthog.reset();
    } catch (error) {
      console.warn('PostHog reset failed:', error);
    }
  }

  private sanitizeProperties(
    properties: Record<string, unknown>
  ): Record<string, unknown> {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(properties)) {
      // Skip sensitive data or functions
      if (typeof value === 'function') continue;
      if (key.toLowerCase().includes('password')) continue;
      if (key.toLowerCase().includes('ssn')) continue;
      if (key.toLowerCase().includes('social')) continue;

      // Convert complex objects to simplified representations
      if (value && typeof value === 'object' && value.constructor === Object) {
        sanitized[key] = this.sanitizeProperties(
          value as Record<string, unknown>
        );
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(item =>
          typeof item === 'object' && item
            ? this.sanitizeProperties(item as Record<string, unknown>)
            : item
        );
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }
}
